/**
 * ConfirmModal - Confirmation Dialog Component
 *
 * A pre-configured modal for confirmation actions like delete, submit, etc.
 * Built on top of the base Modal component with sane defaults.
 *
 * @example
 * <ConfirmModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Listing"
 *   message="Are you sure you want to delete this listing? This action cannot be undone."
 *   confirmText="Delete"
 *   variant="danger"
 * />
 */
import { AlertCircle } from "lucide-react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Box, Text } from "@/shared/ui/base";

export interface ConfirmModalProps {
  /** Modal open state */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Confirm action handler */
  onConfirm: () => void;
  /** Dialog title */
  title: string;
  /** Confirmation message */
  message: string;
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Visual variant (affects button styling) */
  variant?: "danger" | "primary" | "warning";
  /** Loading state during confirmation */
  loading?: boolean;
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "primary",
  loading = false,
}: ConfirmModalProps) {
  const handleConfirm = () => {
    onConfirm();
    // Let parent handle closing after async operation
  };

  // Map variant to button variant
  const buttonVariant = variant === "danger" ? "destructive" : "primary";

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Overlay>
        <Modal.Content size="sm">
          <Modal.Header>
            <Box className="flex items-center gap-3">
              {variant === "danger" && (
                <Box className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </Box>
              )}
              {variant === "warning" && (
                <Box className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                </Box>
              )}
              <Modal.Title>{title}</Modal.Title>
            </Box>
            <Modal.Close />
          </Modal.Header>

          <Modal.Body>
            <Text variant="body" color="secondary">
              {message}
            </Text>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="ghost" onClick={onClose} disabled={loading}>
              {cancelText}
            </Button>
            <Button
              variant={buttonVariant}
              onClick={handleConfirm}
              disabled={loading}
            >
              {loading ? "Processing..." : confirmText}
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Overlay>
    </Modal.Root>
  );
}
